import { Article, clone, Issue, Style } from '@/constants';
import type { Db } from '@shared/storage';
import { Logger } from '@shared/lib/logger';

import { collectArticleStyleIds } from './article-style-references';

const logger = new Logger('downloaded-issue');

/**
 * Route prefix for the Dexie-backed reader (`pages/downloaded-issue`,
 * `/downloads/issues/$issueId/$articleId`) -- the `IssueContext.basePath`
 * value that distinguishes it from `ONLINE_ISSUE_BASE_PATH` (`@entities/
 * issue`).
 */
export const DOWNLOADED_ISSUE_BASE_PATH = '/downloads/issues';

/**
 * How downloaded issues and their styles are stored:
 *
 * - The issue row and its articles are stored per issue (`db.saveIssue`).
 * - Styles live in ONE central `styles` table keyed by style id, shared by
 *   every downloaded issue -- styles are usually reused across issues, so a
 *   style several issues use is stored exactly once, never duplicated.
 * - Each issue row records which styles it uses (`Issue.styleIds`). That's
 *   the ownership record the shared table itself can't hold.
 *
 * "The styles an issue uses" is the union of two things, because either
 * alone can miss a style the issue's articles need to render:
 *
 * 1. The issue's own style list -- exactly what the online reader renders
 *    it with (`IssueContext.stylesMap`).
 * 2. Every style its stored *articles* reference (`article-style-
 *    references.ts`): `article.style`, component `styles` arrays at any
 *    depth, and each of those styles' `desktop`/`tablet`/`parent` chain.
 *    An article can point at a style its issue's list doesn't include --
 *    e.g. one another issue brought in -- and it still needs it.
 *
 * A removal therefore only deletes a style once no remaining issue lists
 * it *and* no remaining article, in any issue, references it.
 */

/**
 * Persist a whole issue -- metadata, every article and every style it uses
 * -- for offline reading. Driven by the issue reader's "Download issue"
 * button (`widgets/issue-reader/menu/menu-button.tsx`'s
 * `DownloadIssueButton`), and re-run by "refresh content"
 * (`refreshDownloadedIssue`).
 *
 * Styles: each style the issue uses is upserted into the shared table --
 * updated in place if it's already stored (possibly by another issue), added
 * if not. On a re-save (refresh) of an issue already downloaded, styles the
 * issue used before but no longer does are removed too -- unless another
 * downloaded issue still uses them (same rule as `removeDownloadedIssue`).
 *
 * All in one transaction, so a failure part-way leaves the previous state
 * intact rather than an issue with half its styles.
 *
 * Cloned before handing to `saveIssue`: that call mutates its `issue`
 * argument (`delete issue.articles`) and each article (`article.IssueID =
 * ...`) in place, and the caller's copies are still mounted/rendering.
 *
 * `downloadQuery` (`Issue.downloadQuery`'s own doc comment) is stored
 * alongside so "refresh content" can replay the exact same `issueOptions`
 * query -- omit it to keep whatever was already stored.
 *
 * Image caching is a separate, explicit step the caller triggers itself
 * (`PIN_ISSUE_ASSETS`) once this resolves -- this only owns Dexie.
 */
export async function saveDownloadedIssue(
	db: Db,
	issue: Issue,
	articles: Array<Article>,
	options: {
		styles: Array<Style>;
		downloadQuery?: Issue['downloadQuery'];
	},
): Promise<void> {
	const styles = uniqueStyles(options.styles);

	await db.runInTransaction(async () => {
		const previous = await findStoredIssue(db, issue.id);

		await db.saveStyles(styles);
		// Resolved after the styles are stored, so chains through the
		// issue's own styles (and through styles already stored by other
		// issues) are followed.
		const referenced = await collectArticleStyleIds(db, articles);
		const styleIds = [
			...new Set([
				...styles.map((style) => `${style.id}`),
				...referenced,
			]),
		];
		const droppedIds = previous?.styleIds
			? previous.styleIds.filter((id) => !styleIds.includes(id))
			: [];

		await db.saveIssue(
			clone({
				...issue,
				articles,
				styleIds,
				downloadQuery: options.downloadQuery ?? issue.downloadQuery,
			}),
		);

		if (droppedIds.length) {
			await deleteStylesUnusedByOtherIssues(db, issue.id, droppedIds);
		}
	});
}

/**
 * Remove a downloaded issue, its articles, and every style *only* it used.
 * A style another downloaded issue also uses is left in place.
 *
 * "Used by another issue" means listed in that issue's `styleIds` *or*
 * referenced by any of its stored articles (see the top of this file) --
 * so a style an article elsewhere still points at is never deleted, even
 * if its own issue's list doesn't include it. An issue downloaded before
 * `styleIds` existed has no list; its articles alone say what it uses, and
 * removing one treats every stored style as a candidate.
 *
 * Cached images are left alone: the caller un-pins them itself
 * (`UNPIN_ISSUE_ASSETS`, see `widgets/issue-reader/menu/menu-button.tsx`
 * and `use-downloaded-issues.ts`), a different lifecycle than Dexie rows.
 */
export async function removeDownloadedIssue(
	db: Db,
	issueId: string,
): Promise<void> {
	await db.runInTransaction(async () => {
		const removed = await findStoredIssue(db, issueId);
		if (!removed) {
			await db.deleteIssue(issueId);
			return;
		}

		// A legacy issue has no ownership record, so every stored style is
		// a candidate -- the "still used" check below is what protects the
		// ones any other issue needs.
		const candidateIds =
			removed.styleIds ?? (await db.getStyles()).map((s) => `${s.id}`);

		await db.deleteIssue(removed.id);
		await deleteStylesUnusedByOtherIssues(db, removed.id, candidateIds);
	});
}

/**
 * The styles the offline reader needs for this downloaded issue, as the
 * `stylesMap` the article renderer takes: `issue.styleIds` plus anything
 * its articles reference (following style chains). A legacy issue without
 * `styleIds` gets every stored style (what the reader always loaded before
 * ownership was recorded).
 */
export async function loadDownloadedIssueStyles(
	db: Db,
	issue: Issue,
	articles: Array<Article>,
): Promise<Map<string, Style>> {
	let styles: Array<Style>;
	if (issue.styleIds) {
		const referenced = await collectArticleStyleIds(db, articles);
		styles = await db.getStylesByIds([
			...new Set([...issue.styleIds, ...referenced]),
		]);
	} else {
		styles = await db.getStyles();
	}
	const map = new Map<string, Style>();
	for (const style of styles) {
		map.set(`${style.id}`, style);
	}
	return map;
}

/**
 * Every style id used by a downloaded issue other than `issueId` -- listed in
 * that issue's `styleIds` *or* referenced by any of its stored articles
 * (following style chains). Read-only counterpart of
 * `deleteStylesUnusedByOtherIssues`'s own "still used" check, extracted so
 * `entities/downloaded-issue`'s storage calculator (`issue-storage.ts`) can
 * ask the same question without duplicating it -- "is this style exclusive
 * to `issueId`, or shared" is exactly this set's complement.
 */
export async function collectStyleIdsUsedByOtherIssues(
	db: Db,
	issueId: string,
): Promise<Set<string>> {
	const others = (await db.getIssues()).filter((i) => i.id !== issueId);
	const otherArticles = (await db.getAllArticles()).filter(
		(article) => `${article.IssueID}` !== `${issueId}`,
	);

	const stillUsed = new Set(others.flatMap((other) => other.styleIds ?? []));
	for (const id of await collectArticleStyleIds(db, otherArticles)) {
		stillUsed.add(id);
	}
	return stillUsed;
}

/**
 * Deletes each of `candidateIds` that no downloaded issue other than
 * `issueId` still uses -- neither listed in its `styleIds` nor referenced by
 * any of its stored articles (following style chains). Must run inside the
 * caller's transaction, after `issueId`'s own articles/ownership record have
 * been removed or replaced.
 */
async function deleteStylesUnusedByOtherIssues(
	db: Db,
	issueId: string,
	candidateIds: Array<string>,
): Promise<void> {
	if (!candidateIds.length) {
		return;
	}
	const stillUsed = await collectStyleIdsUsedByOtherIssues(db, issueId);

	const exclusive = candidateIds.filter((id) => !stillUsed.has(id));
	if (!exclusive.length) {
		return;
	}
	await db.deleteStyles(exclusive);
	logger.debug(`Removed ${exclusive.length} style(s) no other issue uses`);
}

async function findStoredIssue(db: Db, issueId: string): Promise<Issue | null> {
	return (await db.getIssue(issueId)) ?? (await db.getIssue(issueId, true));
}

function uniqueStyles(styles: Array<Style>): Array<Style> {
	const byId = new Map<string, Style>();
	for (const style of styles) {
		if (style?.id === undefined || style?.id === null) {
			continue;
		}
		byId.set(`${style.id}`, style);
	}
	return [...byId.values()];
}
